import { format, formatDistanceToNow, isValid, parseISO } from 'date-fns';

/** "2024-03-01" or ISO datetime -> "Mar 2024". Empty string when absent. */
export function formatMonth(value: string | null | undefined): string {
  if (!value) return '';
  const d = parseISO(value);
  return isValid(d) ? format(d, 'MMM yyyy') : value;
}

/** ISO date/datetime -> "Mar 1, 2024". Empty string when absent. */
export function formatDate(value: string | null | undefined): string {
  if (!value) return '';
  const d = parseISO(value);
  return isValid(d) ? format(d, 'MMM d, yyyy') : value;
}

/** ISO datetime -> "Mar 1, 2024, 3:04 PM". */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '';
  const d = parseISO(value);
  return isValid(d) ? format(d, 'MMM d, yyyy, h:mm a') : value;
}

/** ISO datetime -> "3 days ago". */
export function formatRelative(value: string | null | undefined): string {
  if (!value) return '';
  const d = parseISO(value);
  return isValid(d) ? formatDistanceToNow(d, { addSuffix: true }) : value;
}

/** ISO datetime -> value for <input type="datetime-local"> (local time). */
export function toDateTimeLocal(value: string | null | undefined): string {
  if (!value) return '';
  const d = parseISO(value);
  return isValid(d) ? format(d, "yyyy-MM-dd'T'HH:mm") : '';
}

/** datetime-local input value -> ISO string, or null when empty. */
export function fromDateTimeLocal(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  return isValid(d) ? d.toISOString() : null;
}

/** ISO datetime -> "YYYY-MM-DD" for <input type="date">. */
export function toDateInput(value: string | null | undefined): string {
  if (!value) return '';
  const d = parseISO(value);
  return isValid(d) ? format(d, 'yyyy-MM-dd') : '';
}

export function formatBytes(bytes: number): string {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const n = bytes / Math.pow(1024, i);
  return `${n < 10 && i > 0 ? n.toFixed(1) : Math.round(n)} ${units[i]}`;
}

export function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '0%';
  // Backend may send 0..1 or 0..100; treat <= 1 as a fraction.
  const pct = value <= 1 ? value * 100 : value;
  return `${Math.round(pct)}%`;
}

/** '' -> null, otherwise a number (or null if not parseable). */
export function toNumberOrNull(value: string): number | null {
  if (value.trim() === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** '' -> null, otherwise the string. */
export function toStringOrNull(value: string): string | null {
  return value.trim() === '' ? null : value;
}
